import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCreateGhlConnection } from '@/hooks/useGoHighLevel'
import { createGhlConnectionInputSchema } from '@lib/input/gohighlevel.input'
import type { VenueDto } from '@lib/dto/venue.dto'
import { GhlKeyPermissions } from './GhlKeyPermissions'

interface AddGhlConnectionFormProps {
  venue: VenueDto
}

/**
 * Connect one GoHighLevel sub-account: its Location ID and a key made inside it.
 * The key field is a password field and is emptied once saved — it never comes
 * back from the server, so there is nothing to show again.
 */
export function AddGhlConnectionForm({ venue }: AddGhlConnectionFormProps) {
  const create = useCreateGhlConnection(venue.id)
  const [location, setLocation] = useState('')
  const [key, setKey] = useState('')
  const [trackId, setTrackId] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    const parsed = createGhlConnectionInputSchema.safeParse({
      location,
      key,
      ...(trackId !== '' ? { trackId } : {}),
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the Location ID and key.')
      return
    }
    try {
      await create.mutateAsync(parsed.data)
      setLocation('')
      setKey('')
      setTrackId('')
    } catch (err) {
      setError(requestErrorMessage(err))
    }
  }

  return (
    <Card asChild className="space-y-4 p-4">
      <form onSubmit={(e) => void submit(e)} noValidate>
        <p className="m-0 text-sm font-medium text-ink">Add a sub-account</p>
        <Field
          label="Location ID"
          htmlFor="ghl-location"
          hint="It’s in the sub-account’s address in GoHighLevel: app.gohighlevel.com/v2/location/‹Location ID›/… Paste the ID or the whole link."
        >
          <Input id="ghl-location" value={location} onChange={(e) => setLocation(e.target.value)} autoComplete="off" />
        </Field>
        <div className="space-y-2">
          <Field
            label="Key"
            htmlFor="ghl-key"
            hint="In that sub-account, open Settings → Private Integrations, create one with the permissions below, and copy its key here."
          >
            <Input id="ghl-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" />
          </Field>
          <GhlKeyPermissions />
        </div>
        {venue.tracks.length > 0 ? (
          <Field
            label="Track (optional)"
            htmlFor="ghl-track"
            hint="Events created from this sub-account’s leads start on this track. Leads still appear for every track."
          >
            <Select id="ghl-track" value={trackId} onChange={(e) => setTrackId(e.target.value)}>
              <option value="">None</option>
              {venue.tracks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {error !== null ? <FormError>{error}</FormError> : null}
        <Button type="submit" size="sm" disabled={create.isPending || location.trim() === '' || key.trim() === ''}>
          {create.isPending ? 'Connecting…' : 'Connect'}
        </Button>
      </form>
    </Card>
  )
}
